import { DateTime } from "luxon";
import { useRef, useState } from "react";
import { date } from "zod";
import { LogType } from "../../../../../../common/log_types";
import { time_format_utils } from "../../../../../../common/time_format_utils";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import { trpc } from "../../../utils/trpc";
import { ModifySettingsButton } from "../base_components/modify_settings_button";
import { SeshSearchForm } from "../base_components/sesh_search_form";
import { SeshTextInput } from "../base_components/sesh_text_input";
import { Disabler } from "../disabler";
import { FilterMenu } from "../guild_logs/guild_log_filter";
import { LogIcon } from "../guild_logs/guild_log_icon";
import { GuildLogList } from "../guild_logs/guild_log_list";
import { DetailsPane, GuildLogsPage } from "../guild_logs/guild_log_page";
import { GuildLogText } from "../guild_logs/guild_log_text";
import { GuildLogTitle } from "../guild_logs/guild_log_title";

export function AdminLogsViewer() {
	const [server_id, setServerId] = useState<string | null>(null);

	const [filter, setFilter] = useState([]);
	const [fetch_date] = useState(new Date());

	// Have to cast this to any because useInfiniteQuery only appears if we have strict mode enabled

	const logs_query = (trpc.admin.viewGuildLogs as any).useInfiniteQuery(
		{
			guild_id: server_id,
			from_before_ms: fetch_date.getTime(),
			filter,
		},
		{
			getNextPageParam: (lastPage) => lastPage.next_page,
			refetchOnWindowFocus: false,
			cacheTime: Infinity,
			enabled: !!server_id,
		}
	);

	return (
		<div className="flex flex-col gap-2">
			<div className="w-1/2">
				<SeshSearchForm
					onSubmit={(server_id) => setServerId(server_id)}
					placeholder={"Server ID"}
				/>
			</div>
			{server_id && (
				<div className="flex flex-col gap-1">
					<Disabler disabled={logs_query.isFetching}>
						<FilterMenu filter={filter} setFilter={setFilter} />
					</Disabler>
					<GuildLogList
						key="LogsList"
						logs_query={logs_query}
						logItemCreator={(log) => <AdminLogItem log={log} />}
					/>
				</div>
			)}
		</div>
	);
}

function AdminLogItem(props: {
	log: RouterInferenceHelpers["guild_logs"]["get"]["output"]["logs"][0];
}) {
	const [is_showing_details, setShowDetails] = useState(false);

	return (
		<div
			className="flex cursor-pointer flex-col gap-0 border border-transparent bg-base-light hover:border-sesh-primary"
			onClick={() => setShowDetails(!is_showing_details)}
		>
			<div className="flex w-full items-center gap-4 p-4">
				<div>
					<LogIcon log_type={props.log.type} />
				</div>
				<div className="flex w-full min-w-0 flex-col gap-1">
					<div className="flex w-full min-w-0 flex-wrap items-center gap-4 text-xs text-sesh-dark sm:flex-nowrap">
						<GuildLogTitle log={props.log} />
						<div className="ml-auto flex-shrink-0">
							{time_format_utils.format_time_concise_date_first(
								DateTime.fromJSDate(
									new Date(props.log.created_at)
								)
							)}
						</div>
					</div>
					<div className="flex items-center gap-4 text-sm font-bold sm:text-base">
						{props.log.userId} [{LogType[props.log.type]}]
					</div>
				</div>
				<ModifySettingsButton hide_arrow>
					<div key={is_showing_details.toString()}>
						{is_showing_details ? (
							<i className="fas fa-chevron-down"></i>
						) : (
							<i className="fas fa-chevron-up"></i>
						)}
					</div>
				</ModifySettingsButton>
			</div>
			{is_showing_details && <DetailsPane log={props.log} />}
		</div>
	);
}
